import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useSettings } from '../util/SettingsProvider';

// Helper function to extract a cookie value by name
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

const Dashboard = ({ userId }) => {
    const navigate = useNavigate();
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [activeRange, setActiveRange] = useState('7d');
    const [loading, setLoading] = useState(true);
    const settings = useSettings();
    useEffect(() => { document.title = `Dashboard ~ ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
    const [dashboardData, setDashboardData] = useState({
        totalClicks: 0,
        totalConv: 0,
        totalEarn: "0.00",
        graph: [],
        offers: []
    });

    // Fetch dashboard data from backend API
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const baseUrl = process.env.REACT_APP_API_URL || '';
                const token = getCookie('authToken');

                const response = await axios.get(`${baseUrl}/api/dashboard`, {
                    params: {
                        range: activeRange
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.data) {
                    setDashboardData({
                        totalClicks: response.data.data.totalClicks ?? 0,
                        totalConv: response.data.data.totalConv ?? 0,
                        totalEarn: response.data.data.totalEarn ?? "0.00",
                        graph: response.data.data.graph || [],
                        offers: response.data.data.offers || []
                    });
                }
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
                toast.error(error.response?.data?.msg || "An unexpected error occurred.");
                if (error.response && error.response.status === 401) {
                    navigate('/login');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [userId, activeRange, navigate]);

    // Format dynamic backend graph data into grouped buckets
    const graphData = React.useMemo(() => {
        const rawGraphData = dashboardData.graph || [];
        
        if (activeRange === '24h' && rawGraphData.length > 0) {
            // Group 24 hours into 4 quarters (6 hours each)
            const grouped = [];
            for (let i = 0; i < 4; i++) {
                const chunk = rawGraphData.slice(i * 6, i * 6 + 6);
                grouped.push({
                    day: `Q${i + 1}`,
                    fullLabel: chunk.length > 0 ? `${chunk[0].day} - ${chunk[chunk.length - 1].day}` : `Q${i + 1}`,
                    clicks: chunk.reduce((sum, pt) => sum + (pt.clicks || 0), 0),
                    conv: chunk.reduce((sum, pt) => sum + (pt.conv || 0), 0),
                    earnings: chunk.reduce((sum, pt) => sum + (pt.earn || 0), 0),
                });
            }
            return grouped;
        }

        if (activeRange === '30d' && rawGraphData.length > 0) {
            // Group 30 days into 4 weeks (approx 7-8 days each)
            const grouped = [];
            const chunkSize = Math.ceil(rawGraphData.length / 4);
            for (let i = 0; i < 4; i++) {
                const chunk = rawGraphData.slice(i * chunkSize, (i + 1) * chunkSize);
                if (chunk.length === 0) continue;
                grouped.push({
                    day: `Week ${i + 1}`,
                    fullLabel: `Week ${i + 1} (${chunk[0].day} - ${chunk[chunk.length - 1].day})`,
                    clicks: chunk.reduce((sum, pt) => sum + (pt.clicks || 0), 0),
                    conv: chunk.reduce((sum, pt) => sum + (pt.conv || 0), 0),
                    earnings: chunk.reduce((sum, pt) => sum + (pt.earn || 0), 0),
                });
            }
            return grouped;
        }

        // Default (7d) - map raw data
        const totalPoints = rawGraphData.length;
        return rawGraphData.map((pt, idx) => ({
            day: pt.day || '',
            fullLabel: pt.day || '',
            clicks: pt.clicks || 0,
            conv: pt.conv || 0,
            earnings: pt.earn || 0,
            x: totalPoints > 1 ? (idx / (totalPoints - 1)) * 100 : 0
        }));
    }, [dashboardData.graph, activeRange]);

    // Ensure grouped points have x-coordinates for SVG rendering
    const chartPoints = React.useMemo(() => {
        return graphData.map((pt, idx) => ({
            ...pt,
            x: graphData.length > 1 ? (idx / (graphData.length - 1)) * 100 : 0
        }));
    }, [graphData]);

    // Dynamic SVG Path Generators based on dataset points
    const getPath = (key) => {
        if (!chartPoints || chartPoints.length === 0) return '';
        const maxVal = Math.max(...chartPoints.map((d) => d[key]), 1);

        return chartPoints.reduce((acc, pt, i, arr) => {
            const y = 90 - (pt[key] / maxVal) * 80;
            if (i === 0) return `M ${pt.x},${y}`;

            const prevPt = arr[i - 1];
            const prevY = 90 - (prevPt[key] / maxVal) * 80;
            const cp1X = prevPt.x + (pt.x - prevPt.x) / 2;
            const cp2X = cp1X;

            return `${acc} C ${cp1X},${prevY} ${cp2X},${y} ${pt.x},${y}`;
        }, '');
    };

    const trendingOffers = (dashboardData.offers || []).map((offer) => ({
        id: offer.id,
        title: offer.name || '',
        clicks: offer.clicks || 0,
        epc: offer.clicks > 0 ? `₹${((offer.earn || 0) / offer.clicks).toFixed(1)}` : '₹0',
        img: offer.logo || ''
    }));

    const quickStats = [
        {
            title: 'Total Clicks',
            value: loading ? '...' : (dashboardData.totalClicks || 0).toLocaleString(),
            change: '',
            isPositive: true,
            color: 'purple',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" />
                </svg>
            )
        },
        {
            title: 'Conversions',
            value: loading ? '...' : (dashboardData.totalConv || 0).toLocaleString(),
            change: '',
            isPositive: true,
            color: 'amber',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                    <path d="M16 16h5v5" />
                </svg>
            )
        },
        {
            title: 'Net Earnings',
            value: loading ? '...' : `₹${dashboardData.totalEarn || "0.00"}`,
            change: '',
            isPositive: true,
            color: 'emerald',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
                    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
                </svg>
            )
        }
    ];

    return (
        <>
            <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
                <style>{`
        @keyframes drawWave {
          to { stroke-dashoffset: 0; }
          }
          .animate-draw {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
            animation: drawWave 1.8s ease-out forwards;
            }
            `}</style>

                <div className="p-4 sm:p-8 space-y-8 max-w-[1600px] mx-auto mt-2">
                    {/* Top Metric Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {quickStats.map((stat, idx) => (
                            <div
                                key={idx}
                                className={`group relative bg-zinc-900/90 p-6 rounded-3xl border border-zinc-800 shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-default ${stat.color === 'purple'
                                    ? 'hover:border-purple-500/50 hover:shadow-purple-500/10'
                                    : stat.color === 'amber'
                                        ? 'hover:border-amber-500/50 hover:shadow-amber-500/10'
                                        : 'hover:border-emerald-500/50 hover:shadow-emerald-500/10'
                                    }`}
                            >
                                <div
                                    className={`absolute top-0 right-0 w-28 h-28 -mr-8 -mt-8 rounded-full blur-2xl transition-all duration-500 opacity-20 group-hover:opacity-40 group-hover:scale-125 ${stat.color === 'purple'
                                        ? 'bg-purple-500'
                                        : stat.color === 'amber'
                                            ? 'bg-amber-500'
                                            : 'bg-emerald-500'
                                        }`}
                                />

                                <div className="flex items-center justify-between mb-4">
                                    <div
                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${stat.color === 'purple'
                                            ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                                            : stat.color === 'amber'
                                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                                : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                            }`}
                                    >
                                        {stat.icon}
                                    </div>

                                    {stat.change && (
                                        <span
                                            className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${stat.isPositive
                                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                                                }`}
                                        >
                                            {stat.change}
                                        </span>
                                    )}
                                </div>

                                <p className="text-zinc-400 text-sm font-medium">{stat.title}</p>
                                <h3 className="text-3xl font-extrabold mt-1 tracking-tight text-zinc-100">
                                    {stat.value}
                                </h3>
                            </div>
                        ))}
                    </div>

                    {/* Custom SVG Graph Section */}
                    <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 sm:p-8 shadow-lg relative overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                            <div>
                                <h3 className="text-xl font-bold text-zinc-100">Weekly Performance</h3>
                                <p className="text-sm text-zinc-400 mt-0.5">Smooth wave analysis of your traffic</p>
                            </div>

                            <div className="flex items-center gap-6">
                                {/* Range Switcher */}
                                <div className="flex items-center bg-zinc-950 p-1 rounded-2xl border border-zinc-800 text-xs font-bold">
                                    {['24h', '7d', '30d'].map((range) => (
                                        <button
                                            key={range}
                                            onClick={() => {
                                                setActiveRange(range);
                                                setHoveredPoint(null);
                                            }}
                                            className={`px-3 py-1.5 rounded-xl transition-all duration-200 uppercase ${activeRange === range
                                                ? 'bg-purple-600 text-white font-black shadow-lg shadow-purple-600/20'
                                                : 'text-zinc-400 hover:text-zinc-100'
                                                }`}
                                        >
                                            {range}
                                        </button>
                                    ))}
                                </div>

                                <div className="hidden md:flex flex-wrap gap-4 text-[11px] font-bold uppercase tracking-wider">
                                    <div className="flex items-center gap-1.5 text-purple-400">
                                        <span className="w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse shadow-[0_0_8px_#c084fc]" /> Clicks
                                    </div>
                                    <div className="flex items-center gap-1.5 text-amber-400">
                                        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Conv
                                    </div>
                                    <div className="flex items-center gap-1.5 text-emerald-400">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Earnings
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative h-72 w-full px-2">
                            {/* Interactive Tooltip */}
                            {hoveredPoint && (
                                <div
                                    className="absolute z-30 pointer-events-none transition-all duration-150 ease-out -translate-x-1/2 -translate-y-full mb-3"
                                    style={{
                                        left: `${hoveredPoint.x}%`,
                                        top: '25%'
                                    }}
                                >
                                    <div className="bg-zinc-950/95 backdrop-blur-md text-zinc-100 text-xs rounded-2xl p-3.5 shadow-2xl border border-zinc-800 space-y-1.5 min-w-[130px]">
                                        <p className="font-bold text-purple-300 pb-1 border-b border-zinc-800">
                                            {hoveredPoint.fullLabel} Performance
                                        </p>
                                        <div className="flex items-center justify-between text-purple-400 font-semibold">
                                            <span>Clicks:</span> <span>{hoveredPoint.clicks}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-amber-400 font-semibold">
                                            <span>Conversions:</span> <span>{hoveredPoint.conv}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-emerald-400 font-semibold">
                                            <span>Earnings:</span> <span>₹{hoveredPoint.earnings}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <svg className="w-full h-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 100 100">
                                {/* Grid Lines */}
                                {chartPoints.map((d, i) => (
                                    <line
                                        key={i}
                                        x1={d.x}
                                        y1="0"
                                        x2={d.x}
                                        y2="100"
                                        stroke="#27272a"
                                        strokeWidth="0.5"
                                        strokeDasharray="4"
                                    />
                                ))}

                                {/* Earnings Path (Emerald Green) */}
                                <path
                                    key={`earnings-${activeRange}`}
                                    d={getPath('earnings')}
                                    fill="none"
                                    stroke="#10b981"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    className="animate-draw drop-shadow-[0_0_10px_rgba(16,185,129,0.4)]"
                                />

                                {/* Conversions Path (Amber/Orange) */}
                                <path
                                    key={`conv-${activeRange}`}
                                    d={getPath('conv')}
                                    fill="none"
                                    stroke="#f59e0b"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    className="animate-draw drop-shadow-[0_0_10px_rgba(245,158,11,0.3)]"
                                />

                                {/* Clicks Path (Neon Purple) */}
                                <path
                                    key={`clicks-${activeRange}`}
                                    d={getPath('clicks')}
                                    fill="none"
                                    stroke="#a855f7"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    className="animate-draw drop-shadow-[0_0_12px_rgba(168,85,247,0.6)]"
                                />

                                {/* Hover Interaction Hotspots */}
                                <g>
                                    {chartPoints.map((pt, idx) => (
                                        <rect
                                            key={idx}
                                            x={pt.x - 5}
                                            y="0"
                                            width="10"
                                            height="100"
                                            fill="transparent"
                                            className="cursor-pointer"
                                            onMouseEnter={() => setHoveredPoint(pt)}
                                            onMouseLeave={() => setHoveredPoint(null)}
                                        />
                                    ))}
                                </g>
                            </svg>
                        </div>

                        <div className="flex justify-between mt-6 px-1">
                            {chartPoints.map((d, i) => (
                                <span
                                    key={i}
                                    className={`text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${hoveredPoint?.day === d.day ? 'text-purple-400 font-black' : 'text-zinc-500'
                                        }`}
                                >
                                    {d.day}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Trending Offers Grid */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 bg-purple-500/10 text-purple-400 rounded-xl border border-purple-500/20">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M16 7h6v6" />
                                        <path d="m22 7-8.5 8.5-5-5L2 17" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-zinc-100">Trending Offers</h3>
                            </div>
                            <button className="text-xs font-bold text-purple-400 hover:text-purple-300 transition-colors">
                                View All Offers &rarr;
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {trendingOffers.map((offer) => (
                                <div
                                    key={offer.id}
                                    className="group bg-zinc-900/90 border border-zinc-800 p-5 rounded-3xl flex items-center justify-between hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/5 cursor-pointer relative overflow-hidden"
                                >
                                    <div className="flex items-center gap-4 relative z-10">
                                        <div className="relative">
                                            {offer.img && (offer.img.startsWith('http') || offer.img.startsWith('data:')) ? (
                                                <img
                                                    className="w-14 h-14 bg-zinc-950 rounded-2xl object-cover shadow-inner group-hover:scale-105 transition-transform duration-300 border border-zinc-800"
                                                    src={offer.img}
                                                    alt={offer.title}
                                                />
                                            ) : (
                                                <div className="w-14 h-14 bg-zinc-950 rounded-2xl flex items-center justify-center text-2xl border border-zinc-800">
                                                    {offer.img}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <h4 className="font-bold text-zinc-100 group-hover:text-purple-400 transition-colors duration-200">
                                                {offer.title}
                                            </h4>
                                            <div className="flex gap-2 mt-1.5">
                                                <span className="text-[11px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2.5 py-0.5 rounded-lg tracking-tight">
                                                    {offer.clicks} Clicks
                                                </span>
                                                <span className="text-[11px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-lg tracking-tight">
                                                    {offer.epc} EPC
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        aria-label="View Offer Details"
                                        className="p-3 bg-zinc-950 rounded-2xl text-zinc-400 group-hover:bg-purple-600 group-hover:text-white transition-all duration-300 shadow-sm relative z-10 group-hover:rotate-12 border border-zinc-800 group-hover:border-purple-500"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M15 3h6v6" />
                                            <path d="M10 14 21 3" />
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Dashboard;